import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { auth } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";
import { WebAuthnLoginButton } from "@/components/WebAuthn";

export default function Login() {
  const [u, setU] = useState("");
  const [p, setP] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const redirect = sp.get("redirect");  // e.g. "/action/120530?do=approve&t=..."
  const migrated = sp.get("migrated") === "1";

  // One-shot toast after the PWA self-heals from an origin/backend migration.
  // Banner (below) stays put so the user can read it without racing the toast.
  useEffect(() => {
    if (migrated) {
      toast.info("App updated — please sign in and re-enroll biometrics.", { duration: 6000 });
    }
  }, [migrated]);

  const submit = async (e) => {
    e.preventDefault();
    if (!u || !p) return toast.error("Enter username and password");
    setLoading(true);
    try {
      const { data } = await auth.login(u, p);
      auth.setSession(data.token, {
        emp_no: data.emp_no,
        full_name: data.full_name,
        username: data.username,
        role: data.role,
      });
      toast.success(`Welcome, ${data.full_name} (${data.role})`);
      // Honour a `?redirect=<path>` query — used by the email one-click
      // landing page to bounce the user back after a device-cookie prompt.
      nav(redirect && redirect.startsWith("/") ? redirect : "/home");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-stretch bg-slate-50">
      {/* Left brand panel */}
      <div className="hidden lg:flex w-2/5 bg-gradient-to-br from-cyan-700 via-purple-800 to-cyan-900 text-white flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: "radial-gradient(circle at 30% 30%, #ffffff 1px, transparent 1px), radial-gradient(circle at 70% 70%, #ffffff 1px, transparent 1px)",
          backgroundSize: "32px 32px"
        }} />
        <div className="relative z-10">
          <div className="text-xs tracking-[0.3em] uppercase text-cyan-100 mb-3">Procurement Suite</div>
          <h1 className="text-5xl font-extrabold leading-tight">PR Approval<br/>Dashboard</h1>
          <p className="mt-6 text-cyan-50 max-w-md leading-relaxed">
            Intelligent purchase requisition reviews powered by deterministic price &amp; vendor
            risk scoring with Claude reasoning explanations.
          </p>
        </div>
        <div className="relative z-10 grid grid-cols-3 gap-3 text-sm text-cyan-100">
          <div className="border-l border-purple-400/40 pl-3">
            <div className="text-2xl font-semibold text-white">3</div>
            <div className="text-xs uppercase tracking-wider">Risk Checks</div>
          </div>
          <div className="border-l border-purple-400/40 pl-3">
            <div className="text-2xl font-semibold text-white">MS SQL</div>
            <div className="text-xs uppercase tracking-wider">Live Data</div>
          </div>
          <div className="border-l border-purple-400/40 pl-3">
            <div className="text-2xl font-semibold text-white">AI</div>
            <div className="text-xs uppercase tracking-wider">Reasoning</div>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <Card className="w-full max-w-md border-slate-200 shadow-md">
          <CardHeader>
            <div className="w-11 h-11 rounded-full bg-cyan-100 text-cyan-800 flex items-center justify-center mb-2">
              <ShieldCheck size={22} />
            </div>
            <CardTitle className="text-2xl">Sign in</CardTitle>
            <CardDescription>Use your ULogin credentials to continue.</CardDescription>
          </CardHeader>
          <CardContent>
            {migrated && (
              <div
                data-testid="login-migration-banner"
                className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800"
              >
                <b>App updated.</b> We refreshed your local cache. Please sign in
                once more and re-enroll biometrics if you use fingerprint login.
              </div>
            )}
            <form onSubmit={submit} className="space-y-4" data-testid="login-form">
              <div>
                <Label htmlFor="u" className="text-xs uppercase tracking-wider text-slate-500">
                  Username
                </Label>
                <Input
                  id="u"
                  value={u}
                  onChange={(e) => setU(e.target.value)}
                  placeholder="e.g. op"
                  autoFocus
                  data-testid="login-username"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="p" className="text-xs uppercase tracking-wider text-slate-500">
                  Password
                </Label>
                <Input
                  id="p"
                  type="password"
                  value={p}
                  onChange={(e) => setP(e.target.value)}
                  placeholder="••••••••"
                  data-testid="login-password"
                  className="mt-1"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid="login-submit"
                className="w-full bg-cyan-700 hover:bg-cyan-600"
              >
                {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                Sign in
              </Button>

              <WebAuthnLoginButton
                username={u}
                onAuthenticated={() => nav("/home")}
                disabled={loading}
              />

              <p className="text-xs text-slate-500 text-center pt-2">
                Credentials are validated against the ULogin table in MS SQL.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
